import {
  AppointmentNote,
  AppointmentNoteAddendum,
  SourceDocument,
} from '../types/types'
import { diffNoteText, NoteTextDiff } from './note-text-diff'
import {
  FixedSpecialistOption,
  splitReferToSpecialist,
} from './refer-to-specialist.utils'

export type AmendablePath =
  | 'referToSpecialist'
  | 'labsOrdered'
  | 'imagingOrdered'
  | 'followup'
  | 'noFollowupReason'
  | 'text'
  | 'attachments'

export type AddendumOpInput = {
  path: AmendablePath
  op: 'add' | 'set' | 'remove'
  value?: string | boolean
}

export type AmendValues = {
  referToSpecialist: FixedSpecialistOption[]
  labsOrdered: boolean
  imagingOrdered: boolean
  followup: string
  /** Empty unless the edit withdraws the follow-up; the server derives the rest. */
  noFollowupReason: string
  text: string
  attachments?: SourceDocument[]
  /** Files taken off the current note by this edit. Nothing is deleted. */
  removedAttachmentIds?: string[]
}

/** The server's sentinel for a recorded decline. */
const NO_FOLLOWUP = 'none'

export const AMENDMENT_PATH_LABELS: Record<string, string> = {
  referToSpecialist: 'Referral to specialist',
  labsOrdered: 'Labs ordered',
  imagingOrdered: 'Imaging ordered',
  followup: 'Follow up in',
  noFollowupReason: 'No follow-up reason',
  text: 'Note for patient',
  recommendedProgram: 'Recommended program',
  rxInterest: 'Rx interest',
  rxInterestMedications: 'Rx interest medications',
  sourceTagIds: 'Tags',
}

/** The amend form holds exactly this, so the baseline and the live values cannot drift. */
const uniqueSpecialists = (
  specialists: FixedSpecialistOption[]
): FixedSpecialistOption[] => Array.from(new Set(specialists))

export const noteToAmendValues = (
  note: AppointmentNote | undefined
): AmendValues => ({
  referToSpecialist: uniqueSpecialists(
    splitReferToSpecialist(note?.referToSpecialist).fixed
  ),
  labsOrdered: note?.labsOrdered ?? false,
  imagingOrdered: note?.imagingOrdered ?? false,
  followup: note?.followup ?? '',
  noFollowupReason: note?.noFollowupReason ?? '',
  text: note?.text ?? '',
  ...(note?.attachments?.length
    ? { attachments: note.attachments, removedAttachmentIds: [] }
    : {}),
})

/** referToSpecialist is a set, so it diffs to one add or remove per specialist. */
export const buildAddendumOps = (
  base: AmendValues,
  next: AmendValues
): AddendumOpInput[] => {
  const ops: AddendumOpInput[] = []

  // Deduped, because a stored "X | X" yields a base the set diff cannot match
  // while the summary still reports a change.
  const baseSpecialists = uniqueSpecialists(base.referToSpecialist)
  const nextSpecialists = uniqueSpecialists(next.referToSpecialist)

  nextSpecialists
    .filter((specialist) => !baseSpecialists.includes(specialist))
    .forEach((specialist) => {
      ops.push({ path: 'referToSpecialist', op: 'add', value: specialist })
    })

  baseSpecialists
    .filter((specialist) => !nextSpecialists.includes(specialist))
    .forEach((specialist) => {
      ops.push({ path: 'referToSpecialist', op: 'remove', value: specialist })
    })

  if (next.labsOrdered !== base.labsOrdered) {
    ops.push({ path: 'labsOrdered', op: 'set', value: next.labsOrdered })
  }

  if (next.imagingOrdered !== base.imagingOrdered) {
    ops.push({ path: 'imagingOrdered', op: 'set', value: next.imagingOrdered })
  }

  // followup, followupDate and noFollowupReason are one decision. The server
  // expands whichever of them we send, so sending both would fight itself.
  if (
    next.noFollowupReason &&
    next.noFollowupReason !== base.noFollowupReason
  ) {
    ops.push({
      path: 'noFollowupReason',
      op: 'set',
      value: next.noFollowupReason,
    })
  } else if (next.followup !== base.followup && next.followup !== '') {
    ops.push({ path: 'followup', op: 'set', value: next.followup })
  }

  if (next.text !== base.text) {
    ops.push({ path: 'text', op: 'set', value: next.text })
  }

  const baseRemoved = base.removedAttachmentIds ?? []
  ;(next.removedAttachmentIds ?? [])
    .filter((id) => !baseRemoved.includes(id))
    .forEach((id) => ops.push({ path: 'attachments', op: 'remove', value: id }))

  return ops
}

export const formatAddendumValue = (value: unknown): string => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // The server's allowed op paths include array fields the plugin never writes
  // but does render. Calling trim on one threw inside render and took the whole
  // note screen with it.
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'None'
  }

  if (value === null || value === undefined) {
    return 'None'
  }

  const text = String(value)
  return text.trim() === '' ? 'None' : text
}

export const formatAddendumEditor = (
  addendum: Pick<AppointmentNoteAddendum, 'actorName' | 'actorId'>
): string => addendum.actorName?.trim() || addendum.actorId || 'Unknown'

export type AmendFieldChange = {
  label: string
  /** Absent for an add/remove, where there is no value being replaced. */
  before?: string
  after: string
  /** Set for a file name, so a surface that knows its download link can make it one. */
  documentId?: string
}

export type AmendChangeSummary = {
  fieldChanges: AmendFieldChange[]
  /** Null when the note text is untouched, so the diff block stays hidden. */
  textDiff: NoteTextDiff | null
  any: boolean
}

const formatSpecialists = (specialists: FixedSpecialistOption[]): string => {
  const unique = uniqueSpecialists(specialists)
  return unique.length > 0 ? [...unique].sort().join(', ') : 'None'
}

/**
 * The before-and-after the summary step and the edit history both render. Kept
 * beside buildAddendumOps so a field that cannot produce an op cannot appear
 * here as a saved change.
 */
export const describeAmendChanges = (
  base: AmendValues,
  next: AmendValues
): AmendChangeSummary => {
  const fieldChanges: AmendFieldChange[] = []

  if (
    formatSpecialists(base.referToSpecialist) !==
    formatSpecialists(next.referToSpecialist)
  ) {
    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS.referToSpecialist,
      before: formatSpecialists(base.referToSpecialist),
      after: formatSpecialists(next.referToSpecialist),
    })
  }

  if (next.labsOrdered !== base.labsOrdered) {
    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS.labsOrdered,
      before: formatAddendumValue(base.labsOrdered),
      after: formatAddendumValue(next.labsOrdered),
    })
  }

  if (next.imagingOrdered !== base.imagingOrdered) {
    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS.imagingOrdered,
      before: formatAddendumValue(base.imagingOrdered),
      after: formatAddendumValue(next.imagingOrdered),
    })
  }

  // Mirrors buildAddendumOps: a withdrawal is one change, described by the
  // reason, not by an emptied interval.
  if (
    next.noFollowupReason &&
    next.noFollowupReason !== base.noFollowupReason
  ) {
    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS.followup,
      before: formatAddendumValue(base.followup),
      after: `No follow-up (${next.noFollowupReason})`,
    })
  } else if (next.followup !== base.followup && next.followup !== '') {
    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS.followup,
      before: formatAddendumValue(base.followup),
      after: formatAddendumValue(next.followup),
    })
  }

  const baseRemoved = base.removedAttachmentIds ?? []

  for (const attachment of base.attachments ?? []) {
    if (
      (next.removedAttachmentIds ?? []).includes(attachment.id) &&
      !baseRemoved.includes(attachment.id)
    ) {
      fieldChanges.push({
        label: 'Attachment removed',
        after: attachment.fileName,
      })
    }
  }

  const textDiff =
    next.text !== base.text ? diffNoteText(base.text, next.text) : null

  return {
    fieldChanges,
    textDiff,
    any: fieldChanges.length > 0 || textDiff !== null,
  }
}

/**
 * One saved edit, in the same shape the pre-save summary uses, so a change reads
 * identically before and after it is written.
 */
export const summariseAmendment = (
  addendum: AppointmentNoteAddendum
): AmendChangeSummary => {
  const fieldChanges: AmendFieldChange[] = []
  let textDiff: NoteTextDiff | null = null

  const ops = addendum.ops ?? []
  // The server expands one follow-up decision into up to three ops. The
  // pre-save summary shows it as a single row and this block has to match, or
  // the audit trail contradicts what the clinician confirmed.
  const withdrawal = ops.find(
    (op) => op.path === 'noFollowupReason' && !!op.value
  )
  const followupOp = ops.find((op) => op.path === 'followup')
  const reinstated = !!followupOp?.value && followupOp.value !== NO_FOLLOWUP

  ops.forEach((op) => {
    if (op.path === 'attachments') {
      if (op.op === 'remove') {
        const before = Array.isArray(op.previous)
          ? (op.previous as SourceDocument[])
          : []
        fieldChanges.push({
          label: 'Attachment removed',
          after:
            before.find((file) => file.id === op.value)?.fileName ??
            String(op.value),
          documentId: String(op.value),
        })
        return
      }

      const added = op.value as SourceDocument
      fieldChanges.push({
        label: 'Attachment added',
        after: added.fileName,
        documentId: added.id,
      })
      return
    }

    if (op.path === 'followupDate') {
      return
    }

    if (withdrawal && op.path === 'followup') {
      return
    }

    // Picking an interval on a declined note expands a noFollowupReason clear.
    if (reinstated && op.path === 'noFollowupReason' && !op.value) {
      return
    }

    if (op.path === 'noFollowupReason' && withdrawal) {
      fieldChanges.push({
        label: AMENDMENT_PATH_LABELS.followup,
        before: formatAddendumValue(followupOp?.previous),
        after: `No follow-up (${String(op.value)})`,
      })
      return
    }

    if (op.path === 'text') {
      textDiff = diffNoteText(
        typeof op.previous === 'string' ? op.previous : '',
        typeof op.value === 'string' ? op.value : ''
      )
      return
    }

    // An add or remove carries the whole prior list as `previous` and the one
    // element as `value`. Rendering those either side of an arrow reads as a
    // replacement: "Endocrinology to Dermatology" for what was an addition.
    if (op.op === 'add' || op.op === 'remove') {
      fieldChanges.push({
        label: AMENDMENT_PATH_LABELS[op.path] ?? op.path,
        after: `${op.op === 'add' ? '+' : '−'} ${formatAddendumValue(
          op.value
        )}`,
      })
      return
    }

    fieldChanges.push({
      label: AMENDMENT_PATH_LABELS[op.path] ?? op.path,
      before: formatAddendumValue(op.previous),
      after: formatAddendumValue(op.value),
    })
  })

  return {
    fieldChanges,
    textDiff,
    any: fieldChanges.length > 0 || textDiff !== null,
  }
}
